import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Col, FormControl, Grid, Row } from 'react-bootstrap';
import Empty from '../../components/Empty.jsx';
import { orgStakeholdersContent } from '../../components/EmptyStates.jsx';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import { AddOrgStakeholderLink } from '../../components/links/index.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import {
  retrieveOrgStakeholders,
  searchOrgStakeholders,
  selectSearchOrgStakeholderResults,
  updateSearchQuery,
} from '../../modules/orgStakeholders.js';
import {
  retrieveOrganization,
  selectOrganization,
} from '../../modules/organizations.js';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import { GroupStakeholderIcon } from '../../components/icons/GroupStakeholderIcon.jsx';
import { IndividualStakeholderIcon } from '../../components/icons/IndividualStakeholderIcon.jsx';
import StakeholdersActionsDropdown from '../../components/organizations/Stakeholders/StakeholdersActionsDropdown.jsx';

const mapStateToProps = (state) => {
  return {
    organizationId: selectCurrentOrganizationId(state),
    organization: selectOrganization(state),
    searchResults: selectSearchOrgStakeholderResults(state),
    apiUrl: state.config.apiUrl,
  };
};

const mapDispatchToProps = {
  retrieveOrgStakeholders,
  retrieveOrganization,
  searchOrgStakeholders,
  updateSearchQuery,
};

const OrgStakeholdersContainer = ({
  organizationId,
  searchResults,
  retrieveOrganization,
  organization,
  apiUrl,
  searchOrgStakeholders,
  updateSearchQuery,
}) => {
  const [showEmptyState, setShowEmptyState] = useState(false);
  const groupStakeholders = () =>
    searchResults.filter((stakeholder) => stakeholder.objectType === 'group');
  const individualStakeholders = () =>
    searchResults.filter(
      (stakeholder) => stakeholder.objectType === 'individual'
    );

  const { downloadBlob } = useFetchFile();

  const navigateToAddStakeholder = () =>
    pushRoute('organization/stakeholders/new');

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/organization_stakeholders/structure_csv`,
      null,
      'current-organization-structure CSV',
      'csv'
    );
  };

  const onDownloadErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/organization_stakeholders/errors_csv`,
      null,
      'errors-organization-structure CSV',
      'csv'
    );
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    updateSearchQuery(query);
    searchOrgStakeholders(query);
  };

  useEffect(() => {
    retrieveOrganization(organizationId);
    searchOrgStakeholders('').then(({ entities }) => {
      const hasUnarchived =
        entities.orgStakeholders &&
        Object.values(entities.orgStakeholders).some(
          (obj) => obj.archived === false
        );
      !hasUnarchived && setShowEmptyState(true);
    });
  }, []);

  return (
    <React.Fragment>
      <OrganizationHeader organizationTabs />

      <SubHeader flexJustify small>
        <div className="flex">
          <h2>{organization?.name} Organization Stakeholders</h2>
        </div>
        <div className="flex flex-end" style={{ width: '50%' }}>
          {showEmptyState ? (
            <div className="flex flex-end" style={{ width: '50%' }}>
              <AddOrgStakeholderLink>
                <Button bsStyle="success">
                  <PlusButtonIcon />
                  Create Organization Stakeholders
                </Button>
              </AddOrgStakeholderLink>
            </div>
          ) : (
            <React.Fragment>
              <div
                className="m-r-16 m-l-12"
                style={{ width: '100%', maxWidth: '540px' }}
              >
                <FormControl
                  placeholder="Search"
                  type="text"
                  onChange={handleSearch}
                />
              </div>
              <StakeholdersActionsDropdown
                onDownloadCsv={onDownloadCsv}
                isCanManageStructure={organization?.canManageCustomSettings}
              />
            </React.Fragment>
          )}
        </div>
      </SubHeader>

      {organization?.stakeholdersStructureErrors.error ? (
        <div className="component-xl component-border-bottom text-center">
          <h3 className="text-danger">There was an issue with the CSV.</h3>
          <br />
          <AuthorizedAction action="update" subject="organizationStakeholder">
            <p className="no-margin">
              Please download the CSV error report
              <button
                type="button"
                className="btn-link"
                onClick={onDownloadErrorsCsv}
              >
                here
              </button>
            </p>
          </AuthorizedAction>
        </div>
      ) : null}

      {showEmptyState ? (
        <Empty
          content={orgStakeholdersContent}
          action={navigateToAddStakeholder}
          actionAuthorization={{
            action: 'create',
            subject: 'organizationStakeholder',
          }}
          addBtn
        />
      ) : null}

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {!showEmptyState ? (
                <React.Fragment>
                  <Row className="m-b-16">
                    <Col sm={10} smOffset={1}>
                      <h4 className="text-muted m-b-12">Groups</h4>
                      {groupStakeholders().length
                        ? groupStakeholders().map(({ name, id }) => (
                            <div
                              key={id}
                              className="flex flex-align-center m-b-12 m-l-12"
                            >
                              <GroupStakeholderIcon />
                              <h4 className="m-l-10 m-r-10">{name}</h4>
                            </div>
                          ))
                        : '—'}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={10} smOffset={1}>
                      <h4 className="text-muted m-b-12">Individuals</h4>
                      {individualStakeholders().length
                        ? individualStakeholders().map(
                            ({ firstName, lastName, id }) => (
                              <div
                                key={id}
                                className="flex flex-align-center m-b-12 m-l-12"
                              >
                                <IndividualStakeholderIcon />
                                <h4 className="m-l-10">{firstName}</h4>
                                <h4 className="m-r-10 m-l-10">{lastName}</h4>
                              </div>
                            )
                          )
                        : '—'}
                    </Col>
                  </Row>
                </React.Fragment>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgStakeholdersContainer);
